import type { FC, ReactNode } from 'react';
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router';
import { generatePath } from 'react-router-dom';
import type { AxiosResponse } from 'axios';

import { SURVEY_SELECTION_SORT_BY } from 'src/constants/keyLocalStorage';
import type {
  SortKeys,
  SurveyListItem,
  SurveysGroupedByProjectCategory,
} from 'src/types/survey';
import useLocalStorage from 'src/hooks/useLocalStorage';
import { apiPaths } from 'src/services/api/apiPaths';
import HttpService from 'src/services/api/http';
import { routePaths } from 'src/services/routing/routePaths';
import { useUserContext } from 'src/components/app/context/UserContext';
import ErrorDialog from 'src/components/common/ErrorHandler/ErrorDialog';
import {
  getSortedSurveys,
  groupSurveysByProjectCategory,
} from 'src/components/survey/SurveyContext/helper';
import useFetchSurveyList from 'src/components/survey/SurveyContext/ListContext/useFetchSurveyList';

export interface SurveyListContextProps {
  surveys: SurveyListItem[];
  surveysGroupedByProjectCategory: SurveysGroupedByProjectCategory;
  sortBy: string | null;
  toggleFavoriteSurvey: (surveyId: number) => void;
  sortSurveys: (sortValue?: SortKeys) => void;
  selectSurvey: (surveyId: number) => void;
  loading: boolean;
}

const initialContextValue: SurveyListContextProps = {
  surveys: [],
  surveysGroupedByProjectCategory: {},
  sortBy: 'name',
  toggleFavoriteSurvey: () => {},
  sortSurveys: () => {},
  selectSurvey: () => {},
  loading: false,
};

export const SurveyListContext =
  createContext<SurveyListContextProps>(initialContextValue);

interface SurveyListProviderProps {
  children: ReactNode;
}

export const SurveyListProvider: FC<SurveyListProviderProps> = ({
  children,
}) => {
  const [surveys, setSurveys] = useState<SurveyListItem[]>([]);
  const [surveysGroupedByProjectCategory, setSurveysGroupedByProjectCategory] =
    useState<SurveysGroupedByProjectCategory>({});
  const [sortBy, setSortBy] = useLocalStorage<SortKeys>(
    SURVEY_SELECTION_SORT_BY,
    '-name',
  );
  const { switchSurvey } = useUserContext();
  const [errorMsg, setErrorMsg] = useState('');
  const { t } = useTranslation('survey');
  const history = useHistory();

  const {
    data: surveyList,
    isFetching,
    isLoading,
    error,
  } = useFetchSurveyList();
  const loading = isFetching || isLoading;

  useEffect(() => {
    if (surveyList) {
      const sorted_surveys = getSortedSurveys(sortBy, surveyList);
      setSurveys(sorted_surveys);
      setSurveysGroupedByProjectCategory(
        groupSurveysByProjectCategory(sorted_surveys),
      );
    }
    if (!loading && error) setErrorMsg(t('errors.list'));
  }, [surveyList, error, loading]);

  /**
   * Sort the surveys in the survey state by a key from the survey object.
   * To sort in the opposite direction prefix the key with a minus.
   */
  const sortSurveys = useCallback(
    (sortValue?: SortKeys) => {
      if (!sortValue) return;

      setSortBy(sortValue);
      setSurveys((prev) => getSortedSurveys(sortValue, prev));
    },
    [setSurveys, setSortBy, surveys],
  );

  /**
   * Update a survey in the survey state by passing the survey id and the part of the survey
   * that should be updated as an object. E.g.:
   * updateSurvey(1, {favorite: true})
   */
  const toggleFavoriteSurvey = useCallback((surveyId: number) => {
    const endpoint = generatePath(apiPaths.SURVEY.TOGGLE_FAVORITE, {
      id: surveyId,
    });
    HttpService.patch(endpoint)
      .then(({ data }: AxiosResponse<Pick<SurveyListItem, 'isFavorite'>>) => {
        setSurveys((prev) =>
          prev.reduce((acc: SurveyListItem[], survey) => {
            if (survey.id === surveyId) {
              return [...acc, { ...survey, isFavorite: data.isFavorite }];
            }
            return [...acc, survey];
          }, []),
        );
      })
      .catch((error) => {
        setErrorMsg(error.detail || t('errors.update'));
      });
  }, []);

  /**
   * Selects a survey based on a survey id. To select the survey the backend session has to be updated with
   * the currently selected survey.
   */
  const selectSurvey = useCallback(
    (surveyId: number) => {
      const survey = surveys.find(({ id }) => id === surveyId);
      if (!survey) return;

      switchSurvey(survey.id)
        .then(() => history.push(routePaths.START))
        .catch((error) => {
          setErrorMsg(
            error?.non_field_errors?.join('<br>') || t('errors.selection'),
          );
        });
    },
    [history, surveys, switchSurvey, setErrorMsg, t],
  );

  return (
    <SurveyListContext.Provider
      value={{
        surveys,
        surveysGroupedByProjectCategory,
        sortBy,
        toggleFavoriteSurvey,
        sortSurveys,
        selectSurvey,
        loading,
      }}
    >
      {children}
      <ErrorDialog
        errorMessage={errorMsg}
        isOpen={!!errorMsg}
        onClose={() => setErrorMsg('')}
        severity="error"
      />
    </SurveyListContext.Provider>
  );
};
export const useSurveyList = () => useContext(SurveyListContext);
